import React from 'react';
import { useTranslation } from 'react-i18next';
import type { PhonicsSessionError, ProsodySessionError } from '../../hooks/usePhonicsGuidance';
import './PhonicsSessionSummary.css';

interface PhonicsSessionSummaryProps {
  errors: PhonicsSessionError[];
  prosodyErrors?: ProsodySessionError[];
}

/** Map prosody error type to the prosodyRef type key for display */
const PROSODY_TYPE_MAP: Record<string, string> = {
  weak_form: 'weak_forms',
  contraction: 'connected_speech',
  elision: 'connected_speech',
  linking: 'connected_speech',
  assimilation: 'connected_speech',
};

/** Map prosody error type to the prosody category anchor */
const PROSODY_ANCHOR_MAP: Record<string, string> = {
  weak_form: 'common_weak',
  contraction: 'common_weak',
  elision: 'elision',
  linking: 'linking',
  assimilation: 'assimilation',
};

/** Format category key as display pair */
function formatPair(key: string, isDigraph: boolean): string {
  if (isDigraph) {
    const parts = key.split('_');
    return parts.length === 2 ? `${parts[0]} ↔ ${parts[1]}` : key;
  }
  if (key.startsWith('visual_')) {
    const letters = key.replace('visual_', '');
    return letters.split('').map(l => l.toUpperCase()).join(' ↔ ');
  }
  const parts = key.split('_');
  return parts.length === 2
    ? `${parts[0].toUpperCase()} ↔ ${parts[1].toUpperCase()}`
    : key;
}

const PhonicsSessionSummary: React.FC<PhonicsSessionSummaryProps> = ({ errors, prosodyErrors = [] }) => {
  const { t } = useTranslation(['phonicsRef', 'prosodyRef']);

  if (errors.length === 0 && prosodyErrors.length === 0) return null;

  return (
    <div className="pss">
      {errors.length > 0 && (
        <>
          <div className="pss__header">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18h6" /><path d="M10 22h4" />
              <path d="M12 2a7 7 0 0 0-4 12.7V17h8v-2.3A7 7 0 0 0 12 2z" />
            </svg>
            <span className="pss__title">{t('phonicsRef:sessionSummaryTitle', 'Spelling Patterns to Review')}</span>
          </div>

          <div className="pss__items">
            {errors.map(err => {
              const tipKey = err.isDigraph
                ? `phonicsRef:digraphs.${err.categoryKey}.tip`
                : `phonicsRef:cats.${err.categoryKey}.tip`;
              const tip = t(tipKey, '');
              return (
                <div key={err.categoryKey} className={`pss__item ${err.isDigraph ? 'pss__item--digraph' : ''}`}>
                  <div className="pss__item-header">
                    <span className="pss__pair">{formatPair(err.categoryKey, err.isDigraph)}</span>
                    {err.isDigraph && <span className="pss__badge pss__badge--digraph">{t('phonicsRef:spellingPattern', 'Spelling')}</span>}
                    <span className="pss__count">{t('phonicsRef:sessionErrorCount', '{{count}} times', { count: err.count })}</span>
                  </div>
                  {tip && <p className="pss__tip">{tip}</p>}
                  <a href={`/knowledge/phonics#${err.categoryKey}`} target="_blank" rel="noopener noreferrer" className="pss__link">
                    {t('phonicsRef:learnMore', 'Learn more')} →
                  </a>
                </div>
              );
            })}
          </div>
        </>
      )}

      {prosodyErrors.length > 0 && (
        <>
          <div className="pss__header pss__header--prosody">
            <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
              <path d="M9 18V5l12-2v13" />
              <circle cx="6" cy="18" r="3" /><circle cx="18" cy="16" r="3" />
            </svg>
            <span className="pss__title">{t('phonicsRef:prosodyErrors.title', 'Prosody-Related Listening Patterns')}</span>
          </div>

          <div className="pss__items">
            {prosodyErrors.map(pe => {
              const typeLabel = t(`prosodyRef:type.${PROSODY_TYPE_MAP[pe.type] || pe.type}`, pe.type);
              const errorLabel = t(`phonicsRef:prosodyErrors.${pe.type}`, pe.type);
              const anchor = PROSODY_ANCHOR_MAP[pe.type] || pe.type;
              return (
                <div key={`${pe.type}:${pe.word}`} className="pss__item pss__item--prosody">
                  <div className="pss__item-header">
                    <span className="pss__badge pss__badge--prosody">{typeLabel}</span>
                    <span className="pss__pair">&ldquo;{pe.word}&rdquo;</span>
                    <span className="pss__count">{t('phonicsRef:sessionErrorCount', '{{count}} times', { count: pe.count })}</span>
                  </div>
                  <p className="pss__tip">{errorLabel}</p>
                  <a href={`/knowledge/prosody#${anchor}`} target="_blank" rel="noopener noreferrer" className="pss__link">
                    {t('phonicsRef:learnMore', 'Learn more')} →
                  </a>
                </div>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
};

export default PhonicsSessionSummary;
